/**
 * `gamification-core` — projection of stockpilot `activity` to GameEvents.
 *
 * Gamification is DERIVED from the activity log (`activity_recent` bucket;
 * the master log `activity` is append-only via trigger
 * `activity_append_only_guard` — WITHOUT hash-chain, see mig `00052`): it adds no
 * tables and does not mutate the oplog. `applyEvents` is
 * pure and deterministic → the same feed produces the same `PlayerState` (local on
 * client; recomputable on server for anti-fraud).
 *
 * Maps rows of `activity_recent` (entity_type + action + patch) to domain events
 * declared in `config.ts`. Only entities ACTUALLY tracked by the
 * write-path (`tracked_fields`): `stock_move` (created, with `reason`) and
 * `stock_picking` (updated, `state` field).
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { STOCKPILOT_EVENTS, stockpilotGamificationConfig } from "./config";
import { applyEvents, initialState } from "./engine";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Reason that `validateInventoryCountMutation` stamps on each count move. */
const COUNT_ADJUSTMENT_REASON = "count-adjustment";

/** `client_ts` (honest local time) if present; otherwise `server_ts`. */
function timestampOf(row: Pick<ActivityRow, "client_ts" | "server_ts">): number | null {
  const raw = row.client_ts ?? row.server_ts;
  if (!raw) {
    return null;
  }
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

/** `new` value of a patch field (transition or snapshot of `created`). */
function fieldNewValue(row: Pick<ActivityRow, "patch">, field: string): string | null {
  const change = parseActivityPatch(row.patch)[field];
  if (!change) {
    return null;
  }
  const v = change.new;
  return typeof v === "string" ? v : null;
}

/**
 * Translates the `activity` feed into gamification events (ascending order by
 * time, as required by the streak reducer). Irrelevant rows are ignored.
 */
export function deriveGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const row of rows) {
    const at = timestampOf(row);
    if (at === null) {
      continue;
    }
    const day = dayKeyUTC(at);
    const base = { at, day, meta: { activityId: row.id } };

    if (row.entity_type === "stock_move" && row.action === "created") {
      // A count move (reason count-adjustment) reconciles a discrepancy;
      // any other is an inventory movement. Mutually exclusive.
      if (fieldNewValue(row, "reason") === COUNT_ADJUSTMENT_REASON) {
        events.push({ type: STOCKPILOT_EVENTS.countAdjusted, ...base });
      } else {
        events.push({ type: STOCKPILOT_EVENTS.stockMoved, ...base });
      }
    } else if (
      row.entity_type === "stock_picking" &&
      row.action === "updated" &&
      fieldNewValue(row, "state") === "done"
    ) {
      events.push({ type: STOCKPILOT_EVENTS.pickingConfirmed, ...base });
    }
  }
  // Feed arrives server_ts DESC; reducer requires ASC chronological order.
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Game state derived from the activity feed. Pure: same feed → same
 * state. Returns XP/level/streaks/badges/quests ready for the UI.
 */
export function deriveGameState(rows: readonly ActivityRow[]): PlayerState {
  const events = deriveGameEvents(rows);
  return applyEvents(initialState(), events, stockpilotGamificationConfig).state;
}
